<template>
  <Select
    v-model="currentTimezone"
    :options="timezones"
    :placeholder="$t('timezone.select')"
    :loading="loading"
    option-label="label"
    option-value="value"
    filter
    :filter-placeholder="$t('timezone.search')"
    class="timezone-selector"
  >
    <template #value="slotProps">
      <div v-if="slotProps.value" class="flex items-center gap-2">
        <i class="pi pi-clock"></i>
        <span>{{ getTimezoneLabel(slotProps.value) }}</span>
      </div>
    </template>
    <template #option="slotProps">
      <div class="flex flex-col">
        <span class="font-semibold">{{ slotProps.option.label }}</span>
        <span class="text-sm text-gray-500">{{ slotProps.option.offset }}</span>
      </div>
    </template>
    <template #optiongroup="slotProps">
      <div class="text-primary flex items-center font-bold">
        {{ slotProps.option.label }}
      </div>
    </template>
  </Select>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import Select from 'primevue/select';

import { useAsyncState } from '@vueuse/core';

import { authService } from '@/services/auth';
import { timezoneService, type TimezoneOption } from '@/services/timezone';
import { useAuthStore } from '@/stores/auth';

const authStore = useAuthStore();

const { state: timezones, isLoading: loading } = useAsyncState(
  async () => {
    const result = await timezoneService.getFlatList();
    if (!result.ok) {
      throw new Error(result.error.message);
    }
    return result.data;
  },
  [] as TimezoneOption[],
  {
    onError: e => console.error('Failed to load timezones:', e),
  },
);

const currentTimezone = computed({
  get: () => authStore.currentUser?.timezone || 'UTC',
  set: async (value: string) => {
    try {
      await authService.updateTimezone(value);

      await authStore.fetchUser();
    } catch (error) {
      console.error('Failed to update timezone:', error);
    }
  },
});

const getTimezoneLabel = (value: string): string => {
  const tz = timezones.value.find(t => t.value === value);
  return tz?.label || value;
};
</script>

<style scoped>
.timezone-selector {
  width: 100%;
}
</style>
